"use client";

import type { ProductCard } from "@ellaandil/formatters";
import { Add, Close } from "@ellaandil/ui";
import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "@/components/ui/Button";
import { Swatch } from "@/components/ui/Swatch";
import { Typography } from "@/components/ui/Typography";
import { useBuyBox } from "@/features/products/ProductDetailsPage/useBuyBox";
import { useTranslations } from "@/i18n/client";
import { getDepictVariantId } from "@/lib/depict/product-id";
import { categoryLabel, type GtmItem, variantLabel } from "@/lib/gtm";
import { useCart } from "./hooks";

type UpsellProduct = ProductCard & {
	depictProductId?: string;
};

type Item = NonNullable<ProductCard["items"]>[number];

const isInStock = (stock: Item["stock"]) => stock !== "no" && stock !== 0 && stock != null;

export interface QuickAddPanelProps {
	product: UpsellProduct;
	onClose: () => void;
}

/**
 * Inline "Add product" panel revealed under the cart upsell carousel when the
 * user taps a product's ADD button. Lets them pick a colour (variant) and size
 * without leaving the cart drawer, then commits via the standard buy-box flow.
 *
 * Driven entirely by local state — we intentionally avoid the URL search
 * params the PDP uses (`?size=...`, `?color=...`) because mutating the URL
 * while a modal-style cart drawer is open would clobber the page underneath.
 */
export const QuickAddPanel = ({ product, onClose }: QuickAddPanelProps) => {
	const t = useTranslations();

	// All colour choices, with the merchandised one first so the panel opens
	// pointed at the variant the user just clicked through Depict.
	const allVariants = useMemo<UpsellProduct[]>(() => {
		const variants = (product.variants ?? []) as UpsellProduct[];
		const withoutSelf = variants.filter((v) => v.displayItemId !== product.displayItemId);
		return [product, ...withoutSelf];
	}, [product]);

	const [selectedDisplayItemId, setSelectedDisplayItemId] = useState(product.displayItemId);
	const selectedVariant =
		allVariants.find((v) => v.displayItemId === selectedDisplayItemId) ?? product;

	const items = selectedVariant.items;

	// When the colour changes, drop any stale size selection — sizes are
	// per-variant and a freshly-picked variant may not even stock the size the
	// user had highlighted on the previous one.
	const [selectedSize, setSelectedSize] = useState<string | null>(null);

	const handleVariantSelect = (id: string) => {
		setSelectedDisplayItemId(id);
		setSelectedSize(null);
	};

	const { data: cart } = useCart();
	const selectedSizeItem = items?.find((it) => it.name === selectedSize);
	const gtmItem: GtmItem | undefined = selectedSizeItem
		? {
				item_id: selectedSizeItem.sku ?? selectedVariant.sku ?? "",
				item_name: selectedVariant.name ?? "",
				item_brand: selectedVariant.brandName ?? undefined,
				item_variant: variantLabel(selectedVariant.variantName, selectedSizeItem.name),
				item_category: categoryLabel(selectedVariant.categories),
				price: selectedVariant.priceAsNumber ?? undefined,
				quantity: 1,
			}
		: undefined;

	const buyBox = useBuyBox({
		sizes: items as Item[] | undefined,
		selectedSize: selectedSize ?? undefined,
		depictProductId: getDepictVariantId(items, selectedSize),
		gtmItem,
		gtmCurrency: cart?.currency,
		labels: {
			add_to_cart: t("Add"),
			select_size: t("Select size"),
			out_of_stock: t("Out of stock"),
			coming_soon: t("Coming soon"),
			stock_outside_default_allocation_rule: t("Add"),
		},
		onSuccess: () => {
			onClose();
		},
	});

	// The button always reads "Add" — only its disabled state changes. The buy
	// box would otherwise relabel it per missing step ("Select size"), but the
	// panel pre-selects a colour and only offers in-stock sizes, so the single
	// disabled state here is "no size picked yet". Keep the label steady and let
	// disabled do the talking.
	const buttonProps = buyBox.getButtonProps();

	// Pull the cart drawer's scroll position down to the panel as soon as it
	// mounts — the upsell sits midway through DrawerMain, so without this the
	// panel would unfold off-screen below the carousel.
	const sectionRef = useRef<HTMLElement | null>(null);
	useEffect(() => {
		sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, []);

	// Do not display products not in stock
	if (product.comingSoon) return null;

	return (
		<section
			ref={sectionRef}
			aria-label={t("Add product")}
			className="flex flex-col gap-grid-component-spacing-md rounded-general-sm bg-background-bg-neutral-1 p-grid-component-spacing-md scroll-mt-grid-component-spacing-md"
		>
			<div className="flex items-center justify-between border-b border-border-medium pb-grid-component-spacing-2xs">
				<Typography variant="body-md-reg" as="h3" className="text-text-dark-text-1">
					{t("Add product")}
				</Typography>
				<button
					type="button"
					onClick={onClose}
					aria-label={t("Close add product panel")}
					className="inline-flex min-h-9 min-w-9 cursor-pointer items-center justify-center p-1.5"
				>
					<Close className="size-6" />
				</button>
			</div>

			{allVariants.length > 1 && (
				<div className="flex flex-col gap-grid-component-spacing-2xs">
					<Typography variant="label-sm-reg" className="text-text-dark-text-2 uppercase">
						{t("Select color")}
					</Typography>
					<div className="flex flex-wrap gap-x-grid-component-spacing-md gap-y-grid-component-spacing-2xs">
						{allVariants.map((variant) => {
							const isActive = variant.displayItemId === selectedDisplayItemId;
							return (
								<Swatch
									key={variant.displayItemId}
									hex={variant.swatch?.hex}
									label={variant.variantName ?? variant.name}
									active={isActive}
									aria-pressed={isActive}
									onClick={() => handleVariantSelect(variant.displayItemId)}
								/>
							);
						})}
					</div>
				</div>
			)}

			{items && items.length > 0 && (
				<div className="flex flex-col gap-grid-component-spacing-2xs">
					<Typography variant="label-sm-reg" className="text-text-dark-text-2 uppercase">
						{t("Select size")}
					</Typography>
					<div className="flex flex-wrap gap-grid-component-spacing-3xs">
						{items.map((item) => {
							const isSelected = selectedSize === item.name;
							const available = isInStock(item.stock);
							const ariaParts = [t("Size {{size}}", { size: item.name ?? "" })];
							if (!available) ariaParts.push(t("Out of stock"));
							if (isSelected) ariaParts.push(t("Selected"));
							return (
								<button
									key={item.item}
									type="button"
									onClick={() => item.name && setSelectedSize(item.name)}
									disabled={!available}
									aria-pressed={isSelected}
									aria-label={ariaParts.join(", ")}
									className="flex items-center min-h-10 rounded-radius-component-specific-button p-grid-component-spacing-2xs cursor-pointer disabled:cursor-default"
								>
									<span
										className={twMerge(
											"border-b",
											isSelected ? "border-border-dark" : "border-transparent",
										)}
									>
										<Typography
											variant="body-sm-reg"
											as="span"
											className={twMerge(
												"uppercase",
												available ? "text-text-dark-text-1" : "text-text-dark-text-3",
											)}
										>
											{item.name}
										</Typography>
									</span>
								</button>
							);
						})}
					</div>
				</div>
			)}

			<Button
				variant="solid"
				color="primary"
				size="large"
				disabled={buttonProps.disabled}
				// Button.onClick is `() => void` but the buy-box handler is a
				// `MouseEventHandler` that ignores its event arg — wrap so the
				// types line up without dropping the handler.
				onClick={() => buttonProps.onClick({} as ReactMouseEvent)}
				className="w-full"
			>
				<Add className="size-4" />
				{t("Add")}
			</Button>
		</section>
	);
};
